import * as React from 'react'
import { tw } from 'utils/classnames'
import { useTranslations } from 'next-intl'
import type { UniqueIdentifier, DragEndEvent } from '@dnd-kit/core'
import {
  DndContext,
  useSensor,
  useSensors,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { SpinnerIcon } from 'primitives/icons'

import { SortableItem } from './sortable-items'
import type { UploadedFile } from '../../../_hooks/use-image-upload'
import { useUploadsImages } from '../../../_hooks/use-image-upload'
import { AddImage } from '@web/app/[locale]/clubhouse/assets/icons'
import { IconInfoBorder } from '@repo/design-system-cf/components/icons'

export interface ImageInputProps {
  handleFile?: (files: UploadedFile[]) => void
  id: string
  name?: string
  currentFileCount: number
  images?: UploadedFile[]
  onFilesReordered: (images: UploadedFile[]) => void
  alt?: string
  handleDelete: (e: React.MouseEvent, index: number) => void
  imageUrls: Map<string, string>
  setLoading?: (loading: boolean) => void
  buttonClassName?: string
  maxImages?: number
  inputRef?: React.RefObject<HTMLInputElement | null>
}

const MAX_FILES = 10

export const ImageUploadInput = ({
  handleFile,
  id,
  name,
  currentFileCount,
  images = [],
  onFilesReordered,
  alt = '',
  handleDelete,
  imageUrls,
  setLoading,
  buttonClassName,
  maxImages = MAX_FILES,
  inputRef,
}: ImageInputProps) => {
  const internalRef = React.useRef<HTMLInputElement>(null)
  const hiddenFileInput = inputRef || internalRef
  const t = useTranslations('pages.clubhouse.ask_cf.image_upload')
  const [errorMessage, setErrorMessage] = React.useState<boolean | null>(false)
  const limit = maxImages

  const { uploadImages, loading } = useUploadsImages()

  React.useEffect(() => {
    setLoading?.(loading)
  }, [loading, setLoading])

  const handleClick: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation() // Prevent the event from bubbling up
    if (hiddenFileInput?.current) {
      hiddenFileInput.current.click()
    }
  }

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async event => {
    const filesUploaded = event?.target?.files

    if (filesUploaded && filesUploaded.length + images.length > maxImages) {
      setErrorMessage(true)
      event.target.value = '' // Clear the input
      return
    }

    if (filesUploaded) {
      const newFiles = [...filesUploaded].filter(
        file =>
          !images.some(
            existingFile => existingFile.name === file.name && existingFile.size === file.size,
          ),
      )

      const uploadedFiles = await uploadImages(newFiles)
      const totalFiles = currentFileCount + uploadedFiles.length
      if (totalFiles <= maxImages) {
        handleFile && handleFile(uploadedFiles)
        setErrorMessage(null)
      } else {
        const filesToAdd = uploadedFiles.slice(0, maxImages - currentFileCount)
        handleFile && handleFile(filesToAdd)
        setErrorMessage(true)
      }
    }
    event.target.value = ''
  }

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async e => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = [...e.dataTransfer.files].filter(
        file =>
          !images.some(
            existingFile => existingFile.name === file.name && existingFile.size === file.size,
          ),
      )
      const uploadedFiles = await uploadImages(newFiles)
      const totalFiles = currentFileCount + newFiles.length

      if (totalFiles <= maxImages) {
        handleFile && handleFile(uploadedFiles)
        setErrorMessage(null)
      } else {
        const filesToAdd = uploadedFiles.slice(0, maxImages - currentFileCount)
        handleFile && handleFile(filesToAdd)
        setErrorMessage(true)
      }
      e.dataTransfer.clearData()
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id && images) {
      const oldIndex = images.findIndex(img => img.name === active.id)
      const newIndex = images.findIndex(img => img.name === over.id)
      const newOrder = arrayMove(images, oldIndex, newIndex)
      onFilesReordered(newOrder)
    }
  }

  const preventDefaults: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  React.useEffect(() => {
    if (currentFileCount < MAX_FILES) {
      setErrorMessage(false)
    }
  }, [currentFileCount])

  return (
    <div className="relative">
      {loading ? (
        <div
          className="rounded-2 pointer-events-none absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-gray-400 opacity-50"
          onClick={preventDefaults}
        >
          <SpinnerIcon className="mx-auto animate-spin" />
        </div>
      ) : null}
      <div
        className={tw(
          'flex flex-wrap justify-start gap-2 overflow-hidden xl:gap-4',
          loading && 'pointer-events-none',
        )}
        onClick={preventDefaults}
      >
        {!images?.length ? (
          <button
            type="button"
            onClick={handleClick}
            className={tw(
              'flex h-[42px] w-full flex-row gap-2 rounded-[6px] border py-[11px] pl-2 pr-3',
              buttonClassName,
            )}
            aria-label={t('upload_more_label')}
            disabled={loading}
          >
            <AddImage className="text-cf-violet size-8" />
            <p className="text-cf-grey-600 mt-2 text-xs leading-[18px]">{t('area_label')}</p>
            <p className="text-12 text-cf-grey-600 flex flex-row gap-1 ">
              {t('you_can')}
              <span className="font-bold">{t('upload_limit', { limit: maxImages })}</span>
            </p>
          </button>
        ) : null}
      </div>
      {images.length ? (
        <div
          className={tw('w-full flex-wrap xl:block', loading && 'pointer-events-none')}
          onClick={preventDefaults}
        >
          <div
            onClick={images.length > 0 ? preventDefaults : handleClick}
            className={tw(
              ' bg-cf-gray-100 flex w-full flex-col items-start',
              images.length === 0 && 'cursor-pointer items-center justify-center',
            )}
            onDrop={handleDrop}
            onDragEnter={preventDefaults}
            onDragOver={preventDefaults}
            onDragLeave={preventDefaults}
          >
            <div className="flex items-center justify-start text-cf-grey-600 mb-1">
              <IconInfoBorder className="size-4 mr-0.5" />
              <p>{`You can add up to ${maxImages} photos`}</p>
            </div>
            <div className="relative flex w-full no-scrollbar overflow-x-scroll items-start justify-start gap-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={images.map(img => img.name as UniqueIdentifier)}
                  strategy={rectSortingStrategy}
                >
                  {images?.length
                    ? images.map((image, index) => (
                        <div key={index} className="relative">
                          <SortableItem
                            index={index}
                            imageUrl={imageUrls.get(image.name) || ''}
                            alt={alt}
                            handleDelete={handleDelete}
                            imageName={image.name}
                          />
                        </div>
                      ))
                    : ''}
                  {images?.length < maxImages ? (
                    <button
                      type="button"
                      onClick={handleClick}
                      className="border-cf-grey-250 flex-shrink-0 text-cf-violet bg-cf-grey-25 flex size-[135px] flex-col items-center justify-center gap-1 rounded-2 border border-dashed"
                    >
                      <AddImage className="size-6" />
                      <p className="text-12 font-medium">{t('add_more_label')}</p>
                    </button>
                  ) : null}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      ) : null}
      <input
        id={id}
        name={name}
        type="file"
        onChange={handleChange}
        ref={hiddenFileInput}
        className="absolute h-0 appearance-none overflow-hidden"
        accept="image/png, image/jpeg"
        multiple={maxImages > 1}
        disabled={loading}
      />
      {errorMessage ? (
        <div className="mt-4 flex items-center justify-center">
          <div className="flex w-fit items-center justify-center bg-[#FEF7F5] p-2 align-middle">
            <div className="text-xs font-medium text-[#DA3B07]">
              {t('upload_limit_reached', { limit })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
